import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppointmentsActions } from '../../../constants/actions';
import { AxiosError } from 'axios';
import { AppointmentsApi } from 'integration/api/appointments';
import {
  AppointmentCreationDto,
  AppointmentStatusUpdateDto,
  AppointmentUpdateDto,
} from '@docbay/schemas';
import { WaitingListProps, Props } from './models';
import { PaginationProps } from 'common/types/pagination';

/**
 * Fetches a brief list of the patient's appointments
 * @param params - props containing necessary parameters for fetching the appointments
 * @returns The response data or an error message if something goes wrong
 */
export const fetchPatientAppointmentsBrief = createAsyncThunk(
  AppointmentsActions.fetchPatientsAppointments,
  async (params: Props, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentsApi.fetchPatientAppointmentsBrief(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the list of appointments in waiting
 * @param params - props containing necessary parameters for fetching the waiting list
 * @returns The response data or an error message if something goes wrong
 */
export const fetchAppointmentsWaitingList = createAsyncThunk(
  AppointmentsActions.fetchAppointmentsWaitingList,
  async (params: WaitingListProps, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentsApi.fetchAppointmentsWaitingList(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates a new appointment
 * @param data - DTO containing necessary information about the new appointment to be created
 * @returns The response data or an error message if something goes wrong
 */
export const createAppointment = createAsyncThunk(
  AppointmentsActions.createAppointment,
  async (data: AppointmentCreationDto, { rejectWithValue }) => {
    try {
      const { data: responseData } = await AppointmentsApi.createAppointment(
        data,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the list of patients associated with an appointment
 * @param params - props containing necessary parameters for fetching the patients
 * @returns The response data or an error message if something goes wrong
 */
export const fetchAppointmentPatients = createAsyncThunk(
  AppointmentsActions.fetchAppointmentPatients,
  async (params: { search: string }, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentsApi.fetchAppointmentPatients(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes an existing appointment
 * @param id - the ID of the appointment to be deleted
 * @returns The response data or an error message if something goes wrong
 */
export const deleteAppointment = createAsyncThunk(
  AppointmentsActions.deleteAppointment,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await AppointmentsApi.deleteAppointment(
        id,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches a specific appointment by its ID
 * @param id - the ID of the appointment to be fetched
 * @returns The response data or an error message if something goes wrong
 */
export const fetchAppointmentById = createAsyncThunk(
  AppointmentsActions.fetchAppointmentById,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await AppointmentsApi.fetchAppointmentById(
        id,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Edits an existing appointment
 * @param params - an object containing the ID of the appointment to be edited and a DTO containing the new data for that appointment
 * @returns The response data or an error message if something goes wrong
 */
export const editAppointment = createAsyncThunk(
  AppointmentsActions.editAppointment,
  async (
    params: { id: string; data: AppointmentUpdateDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } = await AppointmentsApi.editAppointment(
        params.id,
        params.data,
      );
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Ends an appointment
 * @param id - the ID of the appointment to be ended
 * @returns The response data or an error message if something goes wrong
 */
export const endAppointment = createAsyncThunk(
  AppointmentsActions.endAppointment,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } = await AppointmentsApi.endAppointment(id);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Updates the status of an appointment
 * @param params - an object containing the ID of the appointment whose status needs to be updated and a DTO containing the new status data for that appointment
 * @returns The response data or an error message if something goes wrong
 */
export const updateAppointmentStatus = createAsyncThunk(
  AppointmentsActions.updateAppointmentStatus,
  async (
    { id, data }: { id: string; data: AppointmentStatusUpdateDto },
    { rejectWithValue },
  ) => {
    try {
      const { data: responseData } =
        await AppointmentsApi.updateAppointmentStatus(id, data);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetches the list of appointment requests
 * @param params - props containing necessary parameters for fetching the appointment requests
 * @returns The response data or an error message if something goes wrong
 */
export const fetchAppointmentRequestList = createAsyncThunk(
  AppointmentsActions.fetchAppointmentRequestList,
  async (params: PaginationProps, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentsApi.fetchAppointmentRequestList(params);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes an existing appointment request
 * @param id - the ID of the appointment request to be deleted
 * @returns The response data or an error message if something goes wrong
 */
export const deleteAppointmentRequest = createAsyncThunk(
  AppointmentsActions.deleteAppointmentRequest,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data: responseData } =
        await AppointmentsApi.deleteAppointmentRequest(id);
      return responseData;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
